import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Select,
  Spin,
  Icon,
  Checkbox,
  Popconfirm,
  message
} from 'antd';
import { get, del } from '../../../utils/httpRequests';
import { APIs } from '../../../utils/apis';
import moment from 'moment';

const antIcon = <Icon type="loading" style={{ fontSize: 24 }} spin />;
const columns = [
  {
    title: 'Enterprise',
    width: 20
  },
  {
    title: 'Due Date',
    width: 20
  },
  {
    title: 'Active',
    width: 10
  },
  {
    title: 'Created At',
    width: 15
  },
  {
    title: 'Updated At',
    width: 15
  },
  {
    title: 'Edit',
    width: 20
  }
];

const { Option } = Select;

const List = () => {
  const [state, setState] = useState({
    list: [],
    isFetching: true,
    allChecked: false
  });

  const [enterprises, setEnterprises] = useState({
    list: [],
    isFetching: true,
    enterprise: {}
  });

  const fetch = enterpriseId => {
    get({
      url: `${APIs.DASHBOARD_BILLING_ALERTS}${
        enterpriseId ? `enterpriseId=${enterpriseId}` : ''
      }`
    }).then(response => {
      setState({
        ...state,
        list: response.data.result.list,
        isFetching: false
      });
    });
  };

  useEffect(() => {
    get({
      url: APIs.ACTIVE_REPORTS.META_DATA + '&filters=enterprise'
    }).then(response => {
      if (!response) {
        throw Error(response.statusText);
      }
      const sortedEnterprises =
        response.data.result.enterprises.sort((a, b) =>
          a?.name?.localeCompare(b.name)
        ) || [];
      setEnterprises({
        ...enterprises,
        list: sortedEnterprises,
        isFetching: false
      });
    });
    fetch();
  }, []);

  const reset = () => {
    setEnterprises({ ...enterprises, enterprise: {} });
    fetch();
  };

  const enterpriseNameById = id =>
    enterprises.list?.find(enterprise => enterprise.id === id);

  const handleChange = index => {
    setEnterprises({ ...enterprises, enterprise: enterprises.list[index] });
  };

  const handleSearch = () => {
    fetch(enterprises.enterprise.id);
  };

  const onChangeAll = e => {
    const list = state.list.map(item => {
      item.checked = e.target.checked;
      return item;
    });
    setState({ ...state, list, allChecked: e.target.checked });
  };

  const onChange = index => {
    state.list[index].checked = !state.list[index].checked;
    setState({ ...state });
    const allChecked = state.list.every(enterprise => enterprise.checked);
    setState({ ...state, allChecked });
  };

  const onDelete = async () => {
    const list = state.list.filter(item => item.checked);
    if (list.length === 0) {
      message.warning('No items selected for deletion');
      return;
    }

    try {
      // Create an array of delete requests
      const deletePromises = list.map(item => {
        return del({
          url: `${APIs.DASHBOARD_BILLING_ALERTS}id=${item.id}` // Assuming each item has an 'id' field
        });
      });

      // Wait for all delete requests to complete
      const responses = await Promise.all(deletePromises);

      // Check if all deletes were successful
      const allSuccess = responses.every(
        response => response.data.meta.status === 200
      );

      if (allSuccess) {
        message.success('All selected items were successfully deleted');
        fetch(); // Refresh the list after successful deletion
      } else {
        message.error('One or more items could not be deleted');
      }
    } catch (error) {
      // Handle any errors that occurred during the delete requests
      message.error('An error occurred while deleting items');
    }
  };

  const cancel = () => {
    message.info('Canceled');
  };

  return (
    <div>
      <Row className="margin-bottom-10 view-header">
        <Col span={18}>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select enterprise"
            optionFilterProp="children"
            value={enterprises.enterprise?.name ?? undefined}
            onChange={handleChange}
            filterOption={(input, option) =>
              option.props?.children
                ?.toLowerCase()
                ?.indexOf(input?.toLowerCase()) >= 0
            }
          >
            {enterprises.list.map((enterprise, i) => (
              <Option key={i} value={i}>
                {enterprise.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={6} className="text-right">
          <Button
            className="ant-btn ant-btn-primary margin-right-10"
            disabled={!enterprises.enterprise?.name}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button
            className="ant-btn ant-btn-primary margin-right-10"
            onClick={reset}
          >
            Reset
          </Button>
          <NavLink
            to={`/billing/alerts/create${
              enterprises.enterprise.id ? `/${enterprises.enterprise.id}` : ''
            }`}
            className="ant-btn ant-btn-primary"
          >
            Create Alert
          </NavLink>
        </Col>
      </Row>
      <Row>
        {state.list.length !== 0 ? (
          <Col span={24} className="margin-bottom-10">
            <Popconfirm
              title="Are you sure want to delete selected alerts?"
              onConfirm={onDelete}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
              disabled={state.list.every(item => !item.checked)}
            >
              <Button
                className={`ant-btn ant-btn-primary margin-right-10 ${state.list.every(
                  item => !item.checked
                ) && 'disabled'}`}
              >
                Delete Selected
              </Button>
            </Popconfirm>
          </Col>
        ) : null}
        <Col span={24} className="margin-bottom-10">
          {state.isFetching || enterprises.isFetching ? (
            <Spin indicator={antIcon} />
          ) : (
            <>
              {state.list.length <= 0 ? (
                <div>&nbsp; No Data</div>
              ) : (
                <>
                  <table className={`ant-table ant-table-small width-100pt`}>
                    <thead className="ant-table-thead">
                      <tr>
                        <th className="padding-8">
                          <Checkbox
                            onChange={onChangeAll}
                            checked={state.allChecked}
                          ></Checkbox>
                        </th>
                        {columns.map((item, i) => (
                          <th
                            className="padding-8"
                            key={i}
                            width={`${item.width}%`}
                          >
                            {item.title}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="ant-table-tbody">
                      {state.list.map((item, index) => (
                        <tr key={index}>
                          <td className="padding-8">
                            <Checkbox
                              onChange={() => onChange(index)}
                              checked={item.checked}
                            ></Checkbox>
                          </td>
                          <td className="padding-8">
                            {enterpriseNameById(item.enterpriseId).name}
                          </td>
                          <td className="padding-8">
                            {moment(item.dueDate).format('DD-MMM-YYYY')}
                          </td>
                          <td className="padding-8">
                            {item?.show ? 'Yes' : 'No'}
                          </td>
                          <td className="padding-8">
                            {moment(item.createdAt).format(
                              'DD-MMM-YYYY hh:mm a'
                            )}
                          </td>
                          <td className="padding-8">
                            {moment(item.updatedAt).format(
                              'DD-MMM-YYYY hh:mm a'
                            )}
                          </td>
                          <td className="padding-8">
                            <NavLink to={`/billing/alerts/edit/${item.id}`}>
                              Edit Detail
                            </NavLink>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default List;
